import type { CSSProperties, FC } from 'react';
import { useEffect, useId, useMemo, useRef } from 'react';

import { ChipValueFormatter } from '../../../../formatters/ChipValueFormatter/ChipValueFormatter';
import { useModifiers } from '../../../../styles/hooks/useModifiers';
import type { ChipViewProps } from '../Chip';

import { createValueObserver } from './lib';

import styles from './Chip2d.module.css';

interface CustomCSSProps extends CSSProperties {
  '--chipSize': string;
  '--chipBackground': string;
}

const chipObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
  entries.forEach((entry) => {
    const chip = entry.target as HTMLDivElement;
    chip?.style.setProperty('font-size', `${chip.clientWidth}px`);
  });
});

const valueObserver = createValueObserver();

export const Chip2D: FC<ChipViewProps> = ({ color, locale, size = 100, weight, value, postfix }) => {
  const chipRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<HTMLDivElement>(null);

  const idA = useId();
  const idB = useId();

  const mods = useModifiers(styles, 'chip', { grow: size === 'grow' });

  useEffect(() => {
    if (!chipRef.current || !valueRef.current) return;

    const chip = chipRef.current;
    const value = valueRef.current;
    chipObserver.observe(chip);
    valueObserver.observe(value);
    return () => {
      chipObserver.unobserve(chip);
      valueObserver.unobserve(value);
    };
  }, []);

  const computeStyles = useMemo((): CustomCSSProps => {
    const styles = {} as CustomCSSProps;
    if (size !== 'inherit' && size !== 'grow') {
      styles['--chipSize'] = `${size}px`;
    }

    return styles;
  }, [size]);

  return (
    <span
      className={`${styles.chip__container} ${styles[`chip__container_weight${weight}`]}`}
      style={computeStyles}
      ref={chipRef}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`${styles.chipsvg} ${mods}`}
        width={100}
        height={100}
        viewBox="0 0 100 100"
      >
        <path
          fill={`url(#${idA})`}
          fillRule="evenodd"
          d="M50 100c27.6142 0 50-22.3858 50-50S77.6142 0 50 0 0 22.3858 0 50s22.3858 50 50 50Zm-.0001-13.333c20.2504 0 36.6666-16.4162 36.6666-36.6667S70.2503 13.3335 49.9999 13.3335c-20.2504 0-36.6666 16.4163-36.6666 36.6668 0 20.2505 16.4162 36.6667 36.6666 36.6667Z"
          clipRule="evenodd"
        />
        <ellipse
          strokeWidth="0"
          stroke="#000"
          ry="50.16649"
          rx="49.95415"
          id="svg_1"
          cy="50"
          cx="50"
          fill={color || 'var(--chipBackground)'}
        />
        <path
          fill={`url(#${idB})`}
          d="M86.6665 50.0003c0 20.2505-16.4162 36.6667-36.6666 36.6667-20.2504 0-36.6666-16.4162-36.6666-36.6667s16.4162-36.6668 36.6666-36.6668c20.2504 0 36.6666 16.4163 36.6666 36.6668Z"
        />
        <path
          fill="#fff"
          fillOpacity=".8"
          d="M97.2044 33.5155a50.0007 50.0007 0 0 0-11.7763-18.798l-4.0743 4.0575a44.2498 44.2498 0 0 1 10.4221 16.6362l5.4285-1.8957ZM2.7955 66.4845a50.0009 50.0009 0 0 0 11.7764 18.798l4.0742-4.0575a44.2503 44.2503 0 0 1-10.422-16.6362l-5.4286 1.8957ZM2.7955 33.5155a50.0004 50.0004 0 0 1 11.7764-18.798l4.0743 4.0575A44.2495 44.2495 0 0 0 8.224 35.4112l-5.4285-1.8957ZM97.2044 66.4845a50.0002 50.0002 0 0 1-11.7764 18.798l-4.0742-4.0575a44.2498 44.2498 0 0 0 10.4221-16.6362l5.4285 1.8957ZM38.9234 1.2423a50.0009 50.0009 0 0 1 22.1532 0l-1.2738 5.6072a44.25 44.25 0 0 0-19.6055 0l-1.2739-5.6072ZM61.0765 98.7577a50.0018 50.0018 0 0 1-22.1531 0l1.2738-5.6072a44.2491 44.2491 0 0 0 19.6055 0l1.2738 5.6072Z"
        />
        <path
          fill="#fff"
          fillOpacity=".6"
          d="M15.3076 37.9002c5.0145-14.335 18.6677-24.6488 34.6922-24.6488 16.0266 0 29.6812 10.3166 34.694 24.6543l2.6506-.9253c-5.3841-15.4435-20.0656-26.529-37.3446-26.529-17.2774 0-31.9578 11.0834-37.3431 26.5248l2.6509.924ZM84.6936 62.0954c-5.0131 14.3375-18.6675 24.6535-34.6939 24.6535-16.0241 0-29.6771-10.3132-34.6918-24.6477l-2.6504.9254c5.386 15.44 20.0658 26.5223 37.3423 26.5223 17.2783 0 31.9592-11.0845 37.3438-26.5271l-2.65-.9264Z"
        />
        <path
          fill="#fff"
          fillOpacity=".3"
          d="M99.9834 51.3A50.608 50.608 0 0 0 100 50c0-27.6142-22.3858-50-50-50S0 22.3858 0 50c0 .4347.0055.868.0166 1.3C.706 24.2866 22.8204 2.6 50 2.6c27.1796 0 49.294 21.6866 49.9834 48.7Z"
        />
        <path
          fill="#000"
          fillOpacity=".2"
          d="M.0166 48.7C.0056 49.132 0 49.5653 0 50c0 27.6142 22.3858 50 50 50s50-22.3858 50-50c0-.4346-.0055-.868-.0166-1.3C99.294 75.7134 77.1796 97.4 50 97.4 22.8204 97.4.706 75.7134.0166 48.7Z"
        />
        <defs>
          <linearGradient id={idA} x1="50" x2="49.9911" y1=".9851" y2="100" gradientUnits="userSpaceOnUse">
            <stop stopOpacity="0" />
            <stop offset="1" stopOpacity=".3" />
          </linearGradient>
          <linearGradient id={idB} x1="72.7638" x2="21.6488" y1="21.6576" y2="72.7769" gradientUnits="userSpaceOnUse">
            <stop stopOpacity=".25" />
            <stop offset="1" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>

      <span className={styles.chip__value} ref={valueRef}>
        {ChipValueFormatter.format(locale, value)}
        {postfix}
      </span>
    </span>
  );
};
